import { useCallback, useState } from 'react';
import axios from 'axios';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  CLARIFICATION_PROGRESS,
  CLARIFICATION_STATUS,
} from '../../../shared/offer-domain/clarification';

const toStatusNumber = value => {
  if (value === null || value === undefined || value === '') return 0;
  const key = Number(value);
  return Number.isNaN(key) ? 0 : key;
};

// TE passes procurementMitraId; IC (Mitra side) still passes its own idOffer.
const useClarificationProgressActions = ({
  procurementMitraId,
  idOffer,
} = {}) => {
  const [savingAction, setSavingAction] = useState(null);

  const saveProgress = useCallback(
    async ({
      statusClarification,
      statusClarificationProgress,
      actionKey = 'sendClarification',
      successMessage = 'Clarification sent successfully.',
    } = {}) => {
      if (!procurementMitraId && !idOffer) {
        notify.error('Offer ID is missing. Refresh the page and try again.');
        return false;
      }

      const token = getAuthToken();
      if (!token) {
        notify.error(MESSAGES.authRequired);
        return false;
      }

      setSavingAction(actionKey);
      try {
        await axios.post(
          `${api.API_URL}/procurement/TenderEvaluationClarificationSaveProgress`,
          {
            ...(procurementMitraId ? { procurementMitraId } : { idOffer }),
            statusClarification,
            statusClarificationProgress,
          },
          {
            headers: {
              Accept: '*/*',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        notify.success(successMessage);
        return true;
      } catch (err) {
        notify.apiError(err, 'save clarification progress');
        return false;
      } finally {
        setSavingAction(null);
      }
    },
    [procurementMitraId, idOffer]
  );

  const sendClarification = useCallback(
    status => {
      const key = toStatusNumber(status);
      const fromPanitia = key === CLARIFICATION_STATUS.WAITING_PANITIA;
      return saveProgress({
        statusClarification: CLARIFICATION_STATUS.WAITING_DMUT,
        statusClarificationProgress: fromPanitia
          ? CLARIFICATION_PROGRESS.WAITING_DMUT
          : CLARIFICATION_PROGRESS.OPEN,
        actionKey: 'sendClarification',
        successMessage: 'Clarification sent successfully.',
      });
    },
    [saveProgress]
  );

  const submitIcClarification = useCallback(
    () =>
      saveProgress({
        statusClarification: CLARIFICATION_STATUS.WAITING_PANITIA,
        statusClarificationProgress: CLARIFICATION_PROGRESS.WAITING_PANITIA,
        actionKey: 'submitClarification',
        successMessage: 'Clarification submitted successfully.',
      }),
    [saveProgress]
  );

  const closeClarification = useCallback(async () => {
    if (!procurementMitraId && !idOffer) {
      notify.error('Offer ID is missing. Refresh the page and try again.');
      return false;
    }

    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }

    setSavingAction('closeClarification');
    try {
      await axios.post(
        `${api.API_URL}/procurement/TenderEvaluationClarificationNoClarification`,
        procurementMitraId ? { procurementMitraId } : { idOffer },
        {
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Clarification closed successfully.');
      return true;
    } catch (err) {
      notify.apiError(err, 'close clarification');
      return false;
    } finally {
      setSavingAction(null);
    }
  }, [procurementMitraId, idOffer]);

  return {
    savingAction,
    isSaving: !!savingAction,
    sendClarification,
    submitIcClarification,
    closeClarification,
  };
};

export default useClarificationProgressActions;
